import React, { useState, useEffect } from 'react';

import { message } from 'antd';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { useNodesState, useEdgesState } from 'reactflow';

import TitleBar from '../../../components/TitleBar';
import { FooterButton } from '../../../components/common/FooterButton';
import ReactFlowWrapper from '../../react-flow/ReactFlowWrapper';
import { DependencyMatrix, DivisionData } from './DependencyMatrix';
import { FIND_DIVISION_BY_UUID } from '../queries/DependencyMatrix';
import { FIND_DIVISION_DEPENDENCY_MATRIX } from '../queries/DependenciesTree';
import {
  getNodes,
  getEdges,
  addChildrenToNodeWithParentValidation,
  generateUniqueId,
  getParentNode,
  isNodeAlreadyPopulated,
  clearNodeChildrens,
} from '../../../utils/dependenciesTree';
import { useUserContext } from '../../../context/PermissionsContext';
import TitleUpdater from '../../../components/common/TitleUpdater';

const DependenciesTree = () => {
  const params = useParams();
  const [messageApi, contextHolder] = message.useMessage();

  const [tree, setTree] = useState<any>(null);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [currentLevelSelected, setCurrentLevelSelected] = useState(0);
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [currentParentNode, setCurrentParentNode] = useState<any>(null);

  const { canViewFinancials } = useUserContext();

  const { data: divisionData } = useQuery<DivisionData>(FIND_DIVISION_BY_UUID, {
    variables: {
      uuid: params.divisionId,
    },
  });

  const { data: dependenciesLevelZero } = useQuery<DependencyMatrix>(
    FIND_DIVISION_DEPENDENCY_MATRIX,
    {
      variables: {
        division: {
          uuid: params.divisionId,
        },
        level: 0,
      },
    },
  );

  const { data: dependenciesByLevel } = useQuery<DependencyMatrix>(
    FIND_DIVISION_DEPENDENCY_MATRIX,
    {
      variables: {
        division: {
          uuid: params.divisionId,
        },
        level: currentLevelSelected,
        location: currentNode ? { uuid: currentNode?.data.uuid } : undefined,
        parentLocation: currentParentNode
          ? { uuid: currentParentNode?.data.uuid || '' }
          : undefined,
      },
    },
  );

  const onNodeClick = (_: React.MouseEvent, node: any) => {
    if (!node || node.data.level === 0) {
      return;
    }

    const currentNode = node;
    const parentNode: any = getParentNode(currentNode.id, nodes, edges);

    if (isNodeAlreadyPopulated(tree, node.id)) {
      const newTree = clearNodeChildrens(tree, parentNode?.id, currentNode?.id);
      setTree(newTree);
      return;
    }

    if (node.data.level === 1) {
      setCurrentNode(node);
      setCurrentParentNode(node);
      setCurrentLevelSelected(1);
      return;
    }

    setCurrentNode(currentNode);
    setCurrentParentNode(parentNode);
    setCurrentLevelSelected(node.data.level);
  };

  useEffect(() => {
    if (!divisionData || !dependenciesLevelZero?.findDivisionDependencyMatrix) {
      return;
    }

    const locations =
      dependenciesLevelZero?.findDivisionDependencyMatrix?.division?.locationAuxs.map(
        (location) => {
          return {
            ...location,
            supportedRevenue: canViewFinancials
              ? location.supportedRevenue
              : '--',
            level: 1,
            nodes: [],
            id: generateUniqueId(),
          };
        },
      );
    const newTree = {
      id: generateUniqueId(),
      uuid: divisionData?.findDivisionByUuid?.uuid,
      name: divisionData?.findDivisionByUuid?.name,
      supportedRevenue: canViewFinancials
        ? divisionData?.findDivisionByUuid?.revenue ?? 0
        : '--',
      level: 0,
      nodes: locations,
    };

    setTree(newTree);
  }, [dependenciesLevelZero]);

  useEffect(() => {
    if (!dependenciesByLevel?.findDivisionDependencyMatrix) {
      return;
    }

    if (
      dependenciesByLevel?.findDivisionDependencyMatrix?.division?.locationAuxs
        .length === 0
    ) {
      messageApi.open({
        type: 'error',
        content: 'No more levels no show',
      });
      return;
    }

    const newLevel = currentNode?.data?.level + 1;
    const newNodes =
      dependenciesByLevel?.findDivisionDependencyMatrix?.division?.locationAuxs.map(
        (location: any) => {
          return {
            ...location,
            supportedRevenue: canViewFinancials
              ? location.supportedRevenue
              : '--',
            level: newLevel,
            nodes: [],
            id: generateUniqueId(),
          };
        },
      ) || [];
    const newTree = addChildrenToNodeWithParentValidation(
      tree,
      currentParentNode?.id,
      currentNode?.id,
      newNodes,
    );

    if (newTree) {
      setTree(newTree);
    }
  }, [dependenciesByLevel]);

  useEffect(() => {
    if (!tree) {
      return;
    }

    const newNodes = getNodes(tree);
    const newEdges = getEdges(tree);
    setEdges(newEdges);
    setNodes(newNodes);
  }, [tree]);

  return (
    <div className="flex flex-col w-full h-screen">
      {contextHolder}
      <TitleUpdater title={`Dependency Tree`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`${divisionData?.findDivisionByUuid?.name} Selection`}
      />
      <FooterButton goBack path="/division-selection" text="Back" />
      <ReactFlowWrapper nodes={nodes} edges={edges} onNodeClick={onNodeClick} />
    </div>
  );
};

export default DependenciesTree;
